import { useState } from 'react';
import { useGT } from 'gt-react';
import type { WorkbookDefinition } from '@erp/shared-types';
import { rpcClient } from '@/api/rpc/client';
import { useAgentContainer } from '@/context/agent-container.context';
import { useAgentPanel } from '@/context/agent-panel.context';
import { useAgentState } from '@/context/agent-state.context';
import {
  isStepApplied,
  LIKELY_MATCH,
  workingOnWorkbook,
} from './generationActivity';

/** A regeneration waiting on the user because the AI check says it already matches. */
export interface RegenerateConfirmation {
  stepId: string;
  probability: number;
}

/** What the agent is asked: build the whole process, or apply one step. */
function requestText(
  gt: ReturnType<typeof useGT>,
  stepId: string | null,
  regenerate: boolean,
): string {
  if (!stepId)
    return gt(
      'Build this workbook from its instructions and organize the process into clear, editable steps.',
      { context: 'workbook generate process request' },
    );
  return regenerate
    ? gt(
        'Regenerate the selected process instruction from its text, even though it has not changed, keeping the other steps intact.',
        { context: 'workbook regenerate selected instruction request' },
      )
    : gt(
        'Apply the selected process instruction to this workbook, keeping the other steps intact.',
        { context: 'workbook generate selected instruction request' },
      );
}

export function useWorkbookGeneration(
  workbookId: string,
  definition: WorkbookDefinition,
) {
  const gt = useGT();
  const { moduleConfig } = useAgentContainer();
  const { open } = useAgentPanel();
  const {
    sendMessage,
    isLoading,
    activeToolCalls,
    error: agentError,
  } = useAgentState();
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requested, setRequested] = useState(false);
  const [requestedStepId, setRequestedStepId] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  /** This workbook's own request is streaming. */
  const [sending, setSending] = useState(false);
  const [confirmation, setConfirmation] =
    useState<RegenerateConfirmation | null>(null);
  const ready =
    moduleConfig?.moduleKey === 'workbooks' &&
    moduleConfig.currentPage === `workbook:${workbookId}`;
  // `sending` covers a send still waiting to start (e.g. on a voice save).
  const busy = isLoading || checking || sending;
  const working = workingOnWorkbook({
    checking,
    agentBusy: isLoading,
    askedHere: sending,
    toolCalls: activeToolCalls,
    workbookTools: moduleConfig?.tools,
    ready,
  });
  async function send(stepId: string | null, regenerate: boolean) {
    if (!ready || isLoading || sending) return;
    setRequestError(null);
    setRequested(true);
    setRequestedStepId(stepId);
    if (!stepId) open();
    setSending(true);
    try {
      await sendMessage(
        requestText(gt, stepId, regenerate),
        'workbooks',
        moduleConfig.currentPage,
        {
          ...moduleConfig.getModuleContext?.(),
          generationRequest: { stepId, ...(regenerate && { regenerate }) },
        },
        moduleConfig.pageKey,
        moduleConfig.tools,
      );
    } catch {
      setRequestError(
        gt('Generation could not start. Try again.', {
          context: 'workbook generation request error',
        }),
      );
    }
    setSending(false);
  }
  /**
   * Generates the whole Process, or one Instruction. Regenerating an applied
   * Instruction first asks the AI check; a likely match waits for `confirm`.
   */
  async function generate(stepId?: string) {
    if (!ready || busy) return;
    const step = definition.processSteps.find((item) => item.id === stepId);
    if (!stepId || !step || !isStepApplied(step))
      return send(stepId ?? null, false);
    setRequested(true);
    setRequestError(null);
    setRequestedStepId(stepId);
    setChecking(true);
    // The check is advisory: any failure regenerates as if it had said no.
    const probability = await rpcClient.workbooks
      .instructionMatch({ workbookId, definition, stepId })
      .then((result) => result.probability)
      .catch(() => null);
    setChecking(false);
    if (probability !== null && probability >= LIKELY_MATCH)
      setConfirmation({ stepId, probability });
    else await send(stepId, true);
  }
  return {
    generate,
    confirmation,
    confirm: async () => {
      if (!confirmation) return;
      setConfirmation(null);
      await send(confirmation.stepId, true);
    },
    cancel: () => setConfirmation(null),
    /** True while the AI check runs or the agent is working on this workbook. */
    generating: working,
    /** True while the AI check runs or the agent is working on anything: a
     * new request can't start. */
    busy,
    checking,
    ready,
    requestedStepId,
    error: requested ? requestError || agentError : null,
  };
}
